import {
  Asset,
  DefaultAssetLevel,
  DeployDialogOptions,
  EnplugAccount,
  EnplugUser,
  FeedData,
  Layout,
  ThemeDefinition,
  ThemeAsset,
  FontDefinition,
  FontsApiItem,
  DefaultAsset,
  ZoningAppsResponse,
  ZoningConfig,
  AccountCustomPropertiesResponse,
  AccountInfoResponse,
  LinkedGroup,
  LinkedGroupMembers,
  PaginationConfig,
  PaginationResponse,
  ReorderAssets,
} from './external-types';
import { BridgeBase } from './bridge/bridge-base';
import { validateArray, validateObject, validateString } from './utils';
import { MessageNamespaces, MessageNames } from './internal-types';

/**
 * Account API is a part of the Dashboard SDK used for managing assets, and other app, user and account related data.
 * Use global `enplug.account` object to access these methods.
 *
 * ```typescript title="Example usage of the Account API"
 * const assets = await enplug.account.getAssets();
 * ```
 *
 * Among others, this API allows you to fetch and manipulate the assets:
 * - {@link getAssets|`enplug.account.getAssets()`} - fetch the asset list
 * - {@link getAssetsByIds|`enplug.account.getAssetsByIds()`} - fetch provided assets
 * - {@link saveAsset|`enplug.account.saveAsset()`} - creates/updates an asset, can display Deploy Dialog for editing
 * - {@link deleteAsset|`enplug.account.deleteAsset()`} - removes an asset
 * - {@link touchAsset|`enplug.account.touchAsset()`} - marks asset as recently viewed
 *
 * Other useful commands:
 * - {@link getAccount|`enplug.account.getAccount()`} - fetch current account data
 * - {@link getUser|`enplug.account.getUser()`} - fetch current user data
 * - {@link getThemes|`enplug.account.getThemes()`} - fetches list of themes
 * - {@link getTheme|`enplug.account.getTheme()`} - fetches theme
 * - {@link editTheme|`enplug.account.editTheme()`} - edits the theme
 * - {@link deleteTheme|`enplug.account.deleteTheme()`} - deletes the theme
 */
export class Account {

  /** @internal */
  constructor(private bridge: BridgeBase) { }

  /**
   * Loads all information for the current user. App instance ID,
   * account type, token, account ID, venue ID, and environment.
   */
  getAccount(): Promise<EnplugAccount> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAccount,
    });
  }

  /**
   * Loads all information for the current user.
   */
  getUser(): Promise<EnplugUser> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetUser,
    });
  }

  /**
   * Loads information for the currently selected display group(s).
   *
   * @returns In the account context, it will return all Display Groups in the account;
   * in the Display Group context, it will return only this group’s information.
   */
  getDisplayGroups(): Promise<any[]> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetDisplayGroups,
    });
  }

  getSelectedDisplayId(): Promise<string> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetSelectedDisplayId,
    });
  }

  /**
   * Loads an array of assets for the current app instance.
   *
   * The list is also limited to the {@link setDisplaySelectorCallback|Display Group selector} dropdown's choice.
   *
   * @param T - Model of asset's value
   *
   * @returns Array of assets added by current app filtered by the {@link setDisplaySelectorCallback|Display Group selection} if it is set.
   */
  getAssets<T>(): Promise<Array<Asset<T>>> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAssets,
    });
  }

  /**
   * Loads an array of assets for the current app instance.
   *
   * The list is also limited to the {@link setDisplaySelectorCallback|Display Group selector} dropdown's choice.
   *
   * @param T - Model of asset's value
   * @param options - These options implement the new pagination
   * @returns Array the assets and pagination object
   */
  getAssetsPagination<T>(options: PaginationConfig): Promise<{ Assets: Array<Asset<T>>; Pagination: PaginationResponse }> {
    const params: any = {};
    const booleanDefaults: { [key: string]: boolean } = {
      enableNewPagination: true,
      isPriority: true,
      isInactive: true,
    };

    if (options) {
      for (const key in options) {
        if (options.hasOwnProperty(key)) {
          const value = options[key];
          if (value === undefined) {
            params[key] = booleanDefaults[key] ? false : null;
          } else {
            params[key] = value;
          }
        }
      }
    }

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAssetsPagination,
      params,
    });
  }

  /**
   * Reorders an array of assets for the current app instance.
   *
   * @param data - Object containing view, viewId, appId, and assets order.
   * @returns A promise resolving an array of reordered assets with `Id` and `Order`.
   */
  reorderAssets(data: ReorderAssets): Promise<Array<{ Id: string; Order: number }>> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.ReorderAssets,
      params: {
        ViewId: data.ViewId,
        AppId: data.AppId,
        Assets: data.Assets.map((asset) => ({
          Id: asset.Id,
          Order: asset.Order,
        })),
      },
    });
  }

  /**
   * Loads the linked groups of the current account, to populate a group picker.
   *
   * @returns Resolves to the groups, each with its name and how many layouts it holds.
   */
  getLinkedGroups(): Promise<LinkedGroup[]> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetLinkedGroups,
      params: {},
    });
  }

  /**
   * Moves assets into a linked group, or takes them out of the one they are in.
   *
   * A layout can only join a group when it already has the same zones, the same sizes
   * and the same widgets. The call is rejected as a whole when one of them does not,
   * and the error says which asset and what differs.
   *
   * @param assetIds - Assets to move.
   * @param linkedGroupId - Target group, or null to unlink them.
   * @param linkedGroupName - Only needed when the target group has no members yet.
   *
   * @returns Resolves to the group the assets ended up in, and the ids that changed.
   */
  updateLinkedGroupMembers(
    assetIds: string[],
    linkedGroupId: string | null,
    linkedGroupName?: string,
  ): Promise<LinkedGroupMembers> {
    validateArray(assetIds, 'You must provide the assets to update.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.UpdateLinkedGroupMembers,
      params: {
        assetIds,
        linkedGroupId,
        linkedGroupName,
      },
    });
  }

  /**
   * Loads an array of assets by Ids.
   *
   * @param T - Model of asset's value
   * @param assetsIds - An array of ids of the assets that are to be returned.
   *
   * @returns Resolves to an array of requested assets.
   */
  getAssetsByIds<T>(assetsIds: string[]): Promise<Array<Asset<T>>> {
    validateArray(assetsIds, 'You must provide assetIds array to getAssetsByIds call');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAssetsByIds,
      params: {
        assetsIds,
      },
    });
  }

  /**
   * Loads an array of assets for a specifig app instance.
   * Data is passed as the first param to the success callback.
   *
   * @param T - Model of asset's value
   * @param appId - An id of an app.
   */
  getAssetsForApp<T>(appId: string): Promise<Array<Asset<T>>> {
    validateString(appId, 'You must provide appId string to getAssetsForApp call');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAssetsForApp,
      appId,
    });
  }

  /**
   * Loads a content-approval asset revision.
   *
   * @param T - Model of asset's value
   * @param assetId - ID of an asset
   * @param version - Version of the asset
   *
   * @returns Resolves to the provided revision of the asset.
   */
  getAssetRevision<T>(assetId: string, version: string): Promise<Asset<T>> {
    validateString(assetId, 'You must provide assetId string to getAssetRevision call');
    validateString(version, 'You must provide version string to getAssetRevision call');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAssetRevision,
      params: {
        assetId,
        version,
      },
    });
  }

  /**
   * Creates an asset under the current app instance.
   * @param assets - An array of assets to create.
   * @param dialogOptions - An object with details about displaying the deploy dialog
   * and its content after save.
   *
   * @returns Resolves to list of saved assets as they came from the server.
   */
  bulkCreateAssets<T>(
    assets: Array<Asset<T>>,
    dialogOptions: DeployDialogOptions = {},
  ): Promise<Array<Asset<T>>> {
    validateArray(assets, 'You must provide an array of assets when creating new assets.');
    validateObject(assets[0], 'You must provide an array of assets (objects) when creating new assets.');
    if (assets[0]) {
      validateObject(assets[0].Value, 'You must provide a Value (object) when creating an asset.');
    }
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.BulkCreateAssets,
      params: {
        assets,
        dialogOptions,
      },
    });
  }

  /**
   * Creates an asset under the current app instance.
   * @param assets - An array of assets to deploy.
   * @param dialogOptions - An object with details about displaying the deploy dialog
   * and its content after save.
   *
   * @returns Resolves to list of saved assets as they came from the server.
   */
  bulkDeployAssets<T>(assets: Array<Asset<T>>, dialogOptions: DeployDialogOptions = {}): Promise<Array<Asset<T>>> {
    validateArray(assets, 'You must provide an array of assets when deploying assets.');
    validateObject(assets[0], 'You must provide an array of assets (objects) when deploying assets.');
    if (assets[0]) {
      validateObject(assets[0].Value, 'You must provide a Value (object) when deploying an asset.');
      validateString(assets[0].Id, 'You must provide the ID (string) on the asset you want to update.');
    }
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.BulkDeployAssets,
      params: {
        assets,
        dialogOptions,
      },
    });
  }

  /**
   * Creates or updates an asset. Can also be used to present the Deploy Dialog for changing asset's settings,
   * such as assigning to Display Groups or changing the scheduling.
   *
   * When saving a new asset (`asset.Id` not defined) it will open the Deploy Dialog.
   *
   * ![Deploy Dialog](/img/dashboard-sdk/account/save-asset-deploy-dialog.png)<br />
   * *Deploy Dialog*
   *
   * ```typescript title="Saving the asset"
   * const dialogOptions: DeployDialogOptions = {
   *   showSchedule: true,
   *   scheduleOptions: {
   *     showDuration: true
   *   }
   * };
   *
   * const savedAsset = await enplug.account.saveAsset(asset, dialogOptions);
   * ```
   *
   * Saving an already existing asset will save it instantly without showing the Deploy Dialog.
   *
   * Another useful scenario is to allow the user to assign an existing asset to Display Groups or change the scheduling.
   * To achieve this, you can force showing the Deploy Dialog for an already saved asset, by adjusting the `dialogOptions`.
   * ```typescript title="Opening the Deploy Dialog to assign existing asset to Display Groups"
   * const dialogOptions: DeployDialogOptions = {
   *   showDeployDialog: true, // this forces the Deploy Dialog to appear for an already saved asset
   *   showSchedule: true,
   *   scheduleOptions: {
   *     showDuration: true
   *   }
   * };
   *
   * const savedAsset = await enplug.account.saveAsset(asset, dialogOptions);
   * ```
   * *See more: {@link DeployDialogOptions}*
   *
   * @param T - Model of Asset's value
   * @param asset - An asset to save.
   * @param dialogOptions - Used to configure the Deploy Dialog, which can be shown to
   * assign the asset to Display Groups or modify the scheduling options.
   *
   * @returns Promise resolving with the saved asset.
   */
  saveAsset<T>(asset: Asset<T>, dialogOptions: DeployDialogOptions = {}): Promise<Asset<T>> {
    validateObject(asset, 'You must provide an asset object to save.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.SaveAsset,
      params: {
        asset,
        dialogOptions,
      },
    });
  }

  saveAssetForApp<T>(appId: string, asset: Asset<T>, dialogOptions: DeployDialogOptions = {}) {
    validateString(appId, 'You must provide appId');
    validateObject(asset, 'You must provide an asset to save');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.SaveAsset,
      params: {
        asset,
        appId,
        dialogOptions,
      },
    });
  }

  /**
   * This is for saving an order of assets if needed for the current app. An array of asset IDs
   * is all that is needed, but the implementation also accepts an array of asset objects with `Id` string properties.
   *
   * @param T - Model of asset's value
   * @param assets - An array of assetIds or assets in order which is to be saved
   *
   * @returns Resolves if the order was successfully updated. Rejects otherwise.
   */
  updateAssetOrder<T>(assets: string[] | Array<Asset<T>>): Promise<void> {
    validateArray(assets, 'You must provide an array of assets (or asset ids) in the new order.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.UpdateAssetOrder,
      params: {
        assets,
      },
    });
  }

  /**
   * Deletes one or many assets under the current app instance.
   *
   * @param id - Id of an asset to delete. To delete multiple assets, an array of ids can be passed.
   *
   * @returns Resolves after successfully deleting the assets.
   */
  deleteAsset(id: string | string[]): Promise<void> {
    if (!Array.isArray(id)) {
      validateString(id, 'You must provide the ID (string) of the asset to delete.');
      id = [id];
    } else {
      validateArray(id, 'You must pass a single ID (string) or Array of asset IDs to be deleted.');
      validateString(id[0], 'You must provide at least one Asset ID (string) to be deleted.');
    }
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.DeleteAsset,
      params: {
        ids: id,
      },
    });
  }

  /**
   * Marks asset as recently viewed.
   */
  touchAsset(id: string): Promise<any> {
    validateString(id, 'You must provide an id string to touchAsset call');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.TouchAsset,
      params: { id },
    });
  }

  /**
   * Marks asset as needing approval.
   */
  getAssetNeedsApproval(id: string): Promise<any> {
    validateString(id, 'You must provide an id string to getAssetNeedsApproval call');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAssetNeedsApproval,
      params: { id },
    });
  }

  /**
   * Loads an array of default assets for the current instance's app definition.
   *
   * @param T - Model of asset's value
   * @param level - 'Default', 'Network', 'Account'
   * @param levelId - NetworkId or AccountId
   *
   * @returns Resolves to an array of default assets.
   */
  getDefaultAssets<T>(level: DefaultAssetLevel = 'Default',
                      levelId?: string): Promise<Array<DefaultAsset<T>>> {
    validateString(level, 'You must provide a level parameter that is either "Default", "Network", or "Account"');
    if (level !== 'Account' && level !== 'Network' && level !== 'Default') {
      throw new Error('You must provide a level parameter that is either "Default", "Network", or "Account"');
    }
    if (level === 'Account' || level === 'Network') {
      validateString(levelId, 'You must provide relevant id for the "Account" or "Network" level default assets');
    }

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetDefaultAssets,
      params: {
        level,
        levelId,
      },
    });
  }

  /**
   * @internal
   * Loads list of banner/ticker widget apps.
   */
  getZoningApps(): Promise<ZoningAppsResponse> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetZoningApps,
    });
  }

  /**
   * @internal
   * Loads widget assets used in a given zoning asset layout.
   */
  getZoningLayoutAssets(assetId: string): Promise<Array<Asset<any>>> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetZoningLayoutAssets,
      params: {
        assetId,
      },
    });
  }

  /**
   * @internal
   * Shows modal allowing to pick existing widget or create new.
   * previousAssetId is the asset the slot had before it was emptied, so the modal can point it out.
   */
  acquireZoningWidget(
    zoningAssetId: string, slotConfig: ZoningConfig,
    widgetAppId: string, usedAssetsIds: string[], previousAssetId?: string,
  ): Promise<Asset<any>> {
    validateString(widgetAppId, 'You must provide an widgetAppId parameter');
    validateObject(slotConfig, 'You must provide an slotConfig parameter');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.AcquireZoningWidget,
      params: {
        zoningAssetId,
        slotConfig,
        widgetAppId,
        usedAssetsIds,
        previousAssetId,
      },
    });
  }

  /**
   * @internal
   * Fetches zoning data available after adding/editing a widget.
   */
  editZoningWidget(
    zoningAssetId: string, slotConfig: ZoningConfig,
    widgetAppId: string, widgetAssetId: string,
  ): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.EditZoningWidget,
      params: {
        zoningAssetId,
        slotConfig,
        widgetAppId,
        widgetAssetId,
      },
    });
  }

  /**
   * @internal
   * Fetches zoning data available after adding/editing a widget.
   */
  getZoningData(): Promise<any> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetZoningData,
    });
  }

  /**
   * Loads the list of available fonts for the current app or for specified appId.
   *
   * ```typescript title="Loading the list of fonts"
   * const { fonts } = await enplug.account.getFonts();
   * console.log('List of available fonts', fonts);
   * ```
   *
   * @param appId - ID of app for which the font list should be fetched. If not provided, the fonts for current app will be loaded.
   *
   * @returns Resolves to an object: `{ fonts: FontsApiItem[] }`. See: {@link FontsApiItem}.
   */
  getFonts(appId?: string): Promise<{ fonts: FontsApiItem[] }> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetFonts,
      params: {
        appId,
      },
    });
  }

  /**
   * Loads available themes for the current app or for specified appId.
   */
  getThemes<T>(appId?: string): Promise<Array<ThemeAsset<T>>> {
    return this.bridge.sendMessage<{ themes: Array<ThemeAsset<T>> }>({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetThemes,
      params: {
        appId,
      },
    }).then(({ themes }) => themes);
  }

  /**
   * Loads theme by id.
   * @param themeId - An id of a theme
   */
  getTheme<T>(id: string): Promise<ThemeAsset<T>> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetTheme,
      params: {
        id,
      },
    });
  }

  /**
   * Creates a new theme under the current app instance app definition.
   * The new theme will be available only under the current user's account.
   * @returns callId
   */
  editTheme<AssetValue, FeedType>(
    themeDefinition: ThemeDefinition,
    theme: ThemeAsset<any>,
    previewUrl: string,
    previewAsset?: Array<Asset<AssetValue>>,
    layout?: Layout | ZoningConfig,
    fonts?: FontDefinition[],
    feedData?: FeedData<FeedType>,
  ): Promise<ThemeAsset<AssetValue>> {
    validateObject(themeDefinition, 'You must provide the theme definition (object).');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.EditTheme,
      params: {
        themeDefinition,
        theme,
        previewUrl,
        previewAsset,
        layout,
        fonts,
        feedData,
      },
    });
  }

  /**
   * Creates a new theme under the current app definition.
   * The new theme will be available to all users in the account.
   */
  saveTheme(theme: ThemeAsset<any>, appId?: string): Promise<any> {
    validateObject(theme, 'You must provide the theme (object) to save.');
    validateObject(theme.Value, 'You must provide the theme.Value (object) to save.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.SaveTheme,
      params: {
        theme,
        // Optional: scope the theme to another app, like Zoning duplicating a widget's custom theme.
        // Undefined keeps the current app, same as before.
        appId,
      },
    });
  }

  /**
   * Deletes a theme from the current user's account for
   * the current app definition. Cannot remove default themes.
   */
  deleteTheme(themeId: string): Promise<any> {
    validateString(themeId, 'You must provide the ID (string) of the theme to remove.');
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.DeleteTheme,
      params: {
        themeId,
      },
    });
  }

  /**
   * Returns flag status on account.
   */
  hasFeatureFlag(flagName: string): Promise<any> {
    validateString(flagName, 'You must provide the flag name (string).');
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.HasFeatureFlag,
      params: {
        flagName,
      },
    });
  }

  /**
   * Retrieve all custom display group properties on an account.
   */
  getDisplayGroupCustomProperties(): Promise<AccountCustomPropertiesResponse[]> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetDisplayGroupCustomProperties,
    });
  }

  /**
   * Returns account info such as network type and billing
   */
  loadAccount(): Promise<AccountInfoResponse> {
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.LoadAccount,
    });
  }

  /**
   * Creates an asset history.
   *
   * @param T - Model of Asset's current version value
   * @param asset - An asset to save.
   *
   * @returns Promise resolving with the saved asset version.
   */
  createAssetVersion<T>(asset: Asset<T>): Promise<Asset<T>> {
    validateObject(asset, 'You must provide an asset object to save.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.CreateAssetVersion,
      params: {
        asset,
      },
    });
  }

  /**
   * Loads an array of assetHistory records by Asset Ids.
   *
   * @param assetsIds - An array of ids of the assets which histories will be returned.
   *
   * @returns Resolves to an array of requested hitory assets per assetId.
   */
  getAssetsHistoryByAssetId<T>(assetsIds: string[]): Promise<Array<Asset<T>>> {
    validateArray(assetsIds, 'You must provide assetIds array to getAssetsHistoryByAssetId call');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAssetsHistoryByAssetId,
      params: {
        assetsIds,
      },
    });
  }

  /**
   * Loads an array of assetHistory records by Ids.
   *
   * @param historyIds - An array of ids of the asset histories that will be returned.
   *
   * @returns Resolves to an array of requested hitory assets.
   */
  getAssetsHistoryById<T>(historyIds: string[]): Promise<Array<Asset<T>>> {
    validateArray(historyIds, 'You must provide assetIds array to getAssetsHistoryById call');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.GetAssetsHistoryByAssetId,
      params: {
        historyIds,
      },
    });
  }


  /**
   * Deletes one or many assetHistory records under the current app instance.
   *
   * @param id - Id of an assetHistory to delete. To delete multiple assetsHistories, an array of ids can be passed.
   *
   * @returns Resolves after successfully deleting the assetHistory.
   */
  deleteAssetHistory(id: string | string[]): Promise<void> {
    if (!Array.isArray(id)) {
      validateString(id, 'You must provide the ID (string) of the assetHistory to delete.');
      id = [id];
    } else {
      validateArray(id, 'You must pass a single ID (string) or Array of assetHistory IDs to be deleted.');
      validateString(id[0], 'You must provide at least one AssetHistory ID (string) to be deleted.');
    }
    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.DeleteAssetHistory,
      params: {
        ids: id,
      },
    });
  }

  /**
   * Restores an asset version.
   *
   * @param T - Model of Asset's current version value
   * @param asset - An asset to restore.
   *
   * @returns Promise resolving with the restored asset version.
   */
  restoreVersion<T>(asset: Asset<T>): Promise<Asset<T>> {
    validateObject(asset, 'You must provide an asset snapshot object to restore the version.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.RestoreVersion,
      params: {
        asset,
      },
    });
  }

  duplicateAsset<T>(asset: Asset<T>, dialogOptions: DeployDialogOptions = {}): Promise<Asset<T>> {
    validateObject(asset, 'You must provide an asset object to duplicate.');

    return this.bridge.sendMessage({
      namespace: MessageNamespaces.App,
      name: MessageNames.DuplicateAsset,
      params: {
        asset,
        dialogOptions,
      },
    });
  }
}
